import React, { ReactElement, ReactNode, useContext, useState } from 'react'
import { RechartsComponentData } from '../../../../../Campaign/CampaignDetails/CampaignDetailsChart/types'
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts'
import Colors from '../../../../../../styles/colors.module.scss'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../../store'
import VariableUtils from '../../../../../../utils/variable'
import FlexRow from '../../../../../../components/Layout/FlexRow'
import FlexCol from '../../../../../../components/Layout/FlexCol'
import { VoohImpressions } from '../../types'
import FillingSpinner from '../../../../../../components/FillingSpinner'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { ReactRouterParams } from '../../../../../../types/various'
import { AppContext } from '../../../../../../contexts/AppContext'
import { BaseRole } from '../../../../../../types/user'

const VoohImpressionsChart: React.FC = () => {
  const [focusBar, setFocusBar] = useState<number | null>(null)
  const { t } = useTranslation()
  const { uuid } = useParams<ReactRouterParams>()
  const { userData } = useContext(AppContext)
  const isAdmin =
    !uuid && (userData.user?.roles.some(role => role.baseRole === BaseRole.ADMIN) ?? false)
  const chartData = useSelector((state: RootState) => state.vooCampaignRealization).chartData
  const loading = useSelector((state: RootState) => state.vooCampaignRealization).loading

  const CustomTooltip = ({ active, payload, label }: TooltipProps<any, any>): ReactNode => {
    if (!active || !payload?.length) {
      return null
    }
    const data: VoohImpressions = payload[0].payload

    return (
      <div className='CampaignDetailsChart__tooltip'>
        <div className='CampaignDetailsChart__tooltip--header'>{label}</div>
        <FlexRow className='CampaignDetailsChart__tooltip--row'>
          <FlexCol className='CampaignDetailsChart__tooltip--col'>
            {t('common.numberOfEmissions')}:
          </FlexCol>
          <FlexCol className='CampaignDetailsChart__tooltip--col'>
            {VariableUtils.formatNumber(data.impressions)}
          </FlexCol>
        </FlexRow>
        {/*  /!*todo: temporary hided cumulativeImpressions*!/*/}
        {/*<FlexRow className='CampaignDetailsChart__tooltip--row'>*/}
        {/*  /!*<FlexCol className='CampaignDetailsChart__tooltip--col'>*!/*/}
        {/*  /!*  {t('campaignDetails.chart.sumOfEmissions')}:*!/*/}
        {/*  /!*</FlexCol>*!/*/}
        {/*  /!*<FlexCol className='CampaignDetailsChart__tooltip--col'>*!/*/}
        {/*  /!*  {VariableUtils.formatNumber(data.cumulativeImpressions)}*!/*/}
        {/*  /!*</FlexCol>*!/*/}
        {/*</FlexRow>*/}
        {isAdmin && (
          <>
            <FlexRow className='CampaignDetailsChart__tooltip--row'>
              <FlexCol className='CampaignDetailsChart__tooltip--col'>
                {t('campaignDetails.chart.theoreticalEmissions')}:
              </FlexCol>
              <FlexCol className='CampaignDetailsChart__tooltip--col'>
                {VariableUtils.formatNumber(data.theoreticalEmissions)}
              </FlexCol>
            </FlexRow>
            <FlexRow className='CampaignDetailsChart__tooltip--row'>
              <FlexCol className='CampaignDetailsChart__tooltip--col'>
                {t('voohCampaign.emissions.crmCalculatedEmissions')}:
              </FlexCol>
              <FlexCol className='CampaignDetailsChart__tooltip--col'>
                {VariableUtils.formatNumber(data.crmEmissions)}
              </FlexCol>
            </FlexRow>
          </>
        )}
      </div>
    )
  }

  const Chart = (): ReactElement => {
    const getCellColor = (index: number, focusBar: number | null) => {
      if (focusBar === index) {
        return Colors.blue
      }

      return Colors.chartBar
    }

    const XaxisTick = ({ x, y, payload: { value } }: RechartsComponentData): ReactElement => (
      <g
        className='CampaignDetailsChart__axis-tick'
        transform={`translate(${x},${y})`}
      >
        <text
          x={0}
          y={0}
          dx={-28}
          dy={17}
          textAnchor='start'
        >
          {value}
        </text>
      </g>
    )
    const YaxisTick = (
      { x, y, payload: { value } }: RechartsComponentData,
      customX: number
    ): ReactElement => (
      <g
        className='CampaignDetailsChart__axis-tick'
        transform={`translate(${x},${y})`}
      >
        <text
          x={customX}
          y={0}
          dy={3}
          textAnchor={'start'}
        >
          {value}
        </text>
      </g>
    )
    const yAxisDataKey = (): string => {
      if (!isAdmin) return 'impressions'

      const maxImpressions = Math.max(...chartData.map(d => d.impressions))
      const maxTheoretical = Math.max(...chartData.map(d => d.theoreticalEmissions))
      const maxCrm = Math.max(...chartData.map(d => d.crmEmissions))
      const maxLine = Math.max(maxTheoretical, maxCrm)

      if (maxLine > maxImpressions) {
        if (maxTheoretical >= maxCrm) return 'theoreticalEmissions'
        return 'crmEmissions'
      }
      return 'impressions'
    }

    return (
      <>
        <div className='CampaignDetailsChart__label'>{t('common.numberOfEmissions')}</div>

        <div className='CampaignDetailsChart__chart'>
          <ResponsiveContainer height={320}>
            <ComposedChart
              throttleDelay={100}
              data={chartData}
              maxBarSize={30}
              onMouseMove={({ isTooltipActive, activeTooltipIndex }): void => {
                const payload =
                  isTooltipActive && activeTooltipIndex !== undefined ? activeTooltipIndex : null

                if (payload === focusBar) {
                  return
                }
                setFocusBar(payload)
              }}
            >
              <CartesianGrid
                stroke={Colors.gray2}
                strokeDasharray='2 2'
              />
              <XAxis
                dataKey='day'
                interval='preserveStartEnd'
                stroke={Colors.gray4}
                padding={{ right: 5 }}
                tick={XaxisTick}
              />
              <YAxis
                dataKey={yAxisDataKey()}
                yAxisId={'day'}
                orientation='left'
                stroke={Colors.gray4}
                padding={{ top: 15 }}
                tickLine={false}
                tick={(props): ReactElement => YaxisTick(props, -37)}
              />
              <Tooltip
                cursor={false}
                content={CustomTooltip}
                offset={10}
              />
              <Bar
                dataKey={'impressions'}
                yAxisId={'day'}
                radius={[2, 2, 0, 0]}
                stackId='a'
              >
                {chartData.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getCellColor(index, focusBar)}
                  />
                ))}
              </Bar>
              {isAdmin && (
                <>
                  {/*todo: temporary hided cumulativeImpressions*/}
                  {/*<Line*/}
                  {/*  type='monotone'*/}
                  {/*  yAxisId={'day'}*/}
                  {/*  dataKey='cumulativeImpressions'*/}
                  {/*  stroke={Colors.voohCumulativeImpressions}*/}
                  {/*  dot={false}*/}
                  {/*/>*/}
                  <Line
                    type='monotone'
                    yAxisId={'day'}
                    dataKey='theoreticalEmissions'
                    stroke={Colors.voohTheoreticalEmissions}
                    dot={false}
                  />
                  <Line
                    type='monotone'
                    yAxisId={'day'}
                    dataKey='crmEmissions'
                    stroke={Colors.voohCrmEmissions}
                    dot={false}
                  />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className='CampaignDetailsChart__legend'>
          <div className='CampaignDetailsChart__legend--item'>
            <div className='CampaignDetailsChart__legend--symbol CampaignDetailsChart__legend--symbol-emitted-views' />
            <div>{t('common.numberOfEmissions')}</div>
          </div>
          {isAdmin && (
            <>
              {/*todo: temporary hided cumulativeImpressions*/}
              {/*<div className='CampaignDetailsChart__legend--item'>*/}
              {/*  <div className='CampaignDetailsChart__legend--symbol CampaignDetailsChart__legend--symbol-cumulative-impressions' />*/}
              {/*  <div>{t('campaignDetails.chart.cumulativeImpressions')}</div>*/}
              {/*</div>*/}
              <div className='CampaignDetailsChart__legend--item'>
                <div className='CampaignDetailsChart__legend--symbol CampaignDetailsChart__legend--symbol-theoretical-emissions' />
                <div>{t('campaignDetails.chart.theoreticalEmissions')}</div>
              </div>
              <div className='CampaignDetailsChart__legend--item'>
                <div className='CampaignDetailsChart__legend--symbol CampaignDetailsChart__legend--symbol-crm-emissions' />
                <div>{t('voohCampaign.emissions.crmCalculatedEmissions')}</div>
              </div>
            </>
          )}
        </div>
      </>
    )
  }

  return (
    <div>
      {loading ? (
        <FillingSpinner className='CampaignDetailsChart--loading' />
      ) : chartData.length > 0 ? (
        Chart()
      ) : (
        <div className='CampaignDetailsChart__no-data'>{t('campaignDetails.chart.noData')}</div>
      )}
    </div>
  )
}

export default VoohImpressionsChart
